import {
  useGetApiUserFeedLikeQuery,
  useGetApiUserFeedMatchedQuery,
} from "@shared/index";
import { useEffect, useRef, useState } from "react";
import { REACTIONS_TYPES } from "@shared/constants";
import { ApplicationUserDto } from "@entities/commonTypes";

interface ReactionsPaginationResponse {
  data: ApplicationUserDto[];
  meta?: {
    page: number;
    limit: number;
    counter: number;
  };
}

export const useReactionsPagination = (reactionsType: REACTIONS_TYPES) => {
  const [reactions, setReactions] = useState<ReactionsPaginationResponse>();
  const [page, setPage] = useState(1);
  const [limit] = useState(7);
  const [hasMoreData, setHasMoreData] = useState(true);
  const listEndRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isTriggeringRef = useRef(false);

  const {
    data: likesData,
    error: likesError,
    isError: likesIsError,
    isLoading: likesIsLoading,
    isFetching: likesIsFetching,
  } = useGetApiUserFeedLikeQuery(
    {
      page,
      limit,
    },
    {
      skip: reactionsType !== REACTIONS_TYPES.Likes,
    }
  );

  const {
    data: matchesData,
    error: matchesError,
    isError: matchesIsError,
    isLoading: matchesIsLoading,
    isFetching: matchesIsFetching,
  } = useGetApiUserFeedMatchedQuery(
    {
      page,
      limit,
    },
    {
      skip: reactionsType !== REACTIONS_TYPES.Matches,
    }
  );

  const data =
    reactionsType === REACTIONS_TYPES.Likes ? likesData : matchesData;
  const error =
    reactionsType === REACTIONS_TYPES.Likes ? likesError : matchesError;
  const isError =
    reactionsType === REACTIONS_TYPES.Likes ? likesIsError : matchesIsError;
  const isLoading =
    reactionsType === REACTIONS_TYPES.Likes ? likesIsLoading : matchesIsLoading;
  const isFetching =
    reactionsType === REACTIONS_TYPES.Likes
      ? likesIsFetching
      : matchesIsFetching;

  const processData = (apiData: any, isInitial = false) => {
    if (isInitial) {
      setReactions(apiData);
    } else {
      setReactions((prevData: ReactionsPaginationResponse | undefined) => {
        const existingIds = new Set(
          (prevData?.data || []).map((user) => user.id)
        );
        const newUsers = (apiData.data || []).filter(
          (user: ApplicationUserDto) => !existingIds.has(user.id)
        );

        const newData: ReactionsPaginationResponse = {
          meta: apiData.meta || prevData?.meta,
          data: [...(prevData?.data || []), ...newUsers],
        };
        return newData;
      });
    }

    if (!apiData.data || apiData.data.length === 0) {
      setHasMoreData(false);
    } else if (apiData.data.length < limit) {
      setHasMoreData(false);
    }
  };

  const initializeReactions = () => {
    if (observerRef.current && listEndRef.current) {
      observerRef.current.unobserve(listEndRef.current);
      observerRef.current = null;
    }

    setPage(1);
    setHasMoreData(true);
    setReactions(undefined);
  };

  useEffect(() => {
    initializeReactions();
  }, [reactionsType]);

  useEffect(() => {
    if (error && isError) {
      console.error("Error fetching data:", error);
      setHasMoreData(false);
      return;
    }

    if (data) {
      if (page === 1) {
        processData(data, true);
      } else {
        processData(data, false);
      }
    }
  }, [data, error, isError, page]);

  useEffect(() => {
    if (!reactions || !hasMoreData || isLoading) {
      return;
    }

    if (observerRef.current && listEndRef.current) {
      observerRef.current.unobserve(listEndRef.current);
      observerRef.current = null;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !isLoading &&
          hasMoreData &&
          !isTriggeringRef.current
        ) {
          isTriggeringRef.current = true;
          setPage((prevPage) => prevPage + 1);
        }
      },
      {
        root: null,
        rootMargin: "20px",
        threshold: 0.1,
      }
    );

    if (listEndRef.current) {
      observerRef.current.observe(listEndRef.current);
    }

    return () => {
      if (observerRef.current && listEndRef.current) {
        observerRef.current.unobserve(listEndRef.current);
      }
    };
  }, [isLoading, reactions, hasMoreData]);

  useEffect(() => {
    if (!isLoading && data) {
      isTriggeringRef.current = false;
    }
  }, [isLoading, data]);

  return {
    page,
    limit,
    listEndRef,
    reactions,
    loading: isLoading || isFetching,
    hasMoreData,
    error,
    isError,
  };
};
